import clsx from 'clsx';
import React, { useState, useEffect, useCallback, useMemo } from 'react';

import AnchorLink from '@/src/components/common/Elements/AnchorLink/AnchorLink';
import Icon, { ICON_TYPES, ICONS } from '@/src/components/common/Elements/Icon/Icon';
import Image from '@/src/components/common/Elements/Image/Image';
import SectionLoader, {
  LOADER_TYPE,
} from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import {
  getDocumentContentType,
  getFileEmbedUrl,
  getFilePreviewUrl,
} from '@/src/components/project/Files/Helpers/Helpers';
import { FileCategory, getDocumentType } from '@/src/config/fileTypes';
import { cn } from '@/src/util/ui';

const FILE_FALLBACK_CONTENT = (
  <div
    className={clsx(
      'container--fileNotSupported',
      'tw-absolute tw-inset-0',
      'tw-flex tw-items-center tw-justify-center',
      'tw-text-center',
      'tw-bg-white'
    )}
  >
    <h2 className={clsx('tw-text-cfo-grey-600 !tw-text-[24px] tw-font-brandonTextMedium')}>
      File preview not available
    </h2>
  </div>
);

const getContent = ({
  fileUrl,
  previewUrl,
  fileName,
  documentType,
  onFileLoaded,
}: {
  fileUrl: string;
  previewUrl: string;
  fileName: string;
  documentType: FileCategory;
  onFileLoaded: () => void;
}) => {
  if (documentType === FileCategory.IMAGE) {
    return (
      <div>
        <Image
          className={clsx('img-responsive', 'tw-absolute tw-inset-0', '!tw-m-auto tw-p-[20px]')}
          src={previewUrl}
          onLoad={onFileLoaded}
          fallback={FILE_FALLBACK_CONTENT}
          showLoader={false}
        />
      </div>
    );
  }

  if (documentType === FileCategory.DATA) {
    return FILE_FALLBACK_CONTENT;
  }

  if (documentType === FileCategory.PDF) {
    const documentContentType = getDocumentContentType(fileName);

    return (
      // Use an iframe for PDFs to avoid CSP blocks on <embed>/<object> when object-src is 'none'.
      // Browsers will render PDFs inside an iframe when served with application/pdf.
      <iframe
        title={fileName}
        src={`${fileUrl}`}
        width="100%"
        height="623px"
        frameBorder="0"
        onLoad={onFileLoaded}
        className={clsx('tw-absolute tw-inset-0', 'tw-w-full tw-h-full')}
      />
    );
  }

  if (documentType === FileCategory.DOCUMENT) {
    return (
      <iframe
        title="document"
        src={`https://view.officeapps.live.com/op/embed.aspx?src=${fileUrl}`}
        width="100%"
        height="623px"
        frameBorder="0"
        onLoad={onFileLoaded}
        className={clsx('tw-absolute tw-inset-0', 'tw-w-full tw-h-full')}
      >
        This is an embedded
        <a target="_blank" href="http://office.com" rel="noreferrer">
          Microsoft Office
        </a>{' '}
        document, powered by{' '}
        <a target="_blank" href="http://office.com/webapps" rel="noreferrer">
          Office Online
        </a>
      </iframe>
    );
  }

  return FILE_FALLBACK_CONTENT;
};

export type FileEntryFile = {
  id: number;
  name: string;
  rawName: string;
  googleDriveFileUrl?: string;
  isFileSupported?: boolean;
};

type FileViewerProps = {
  file: FileEntryFile;
  className?: string;
};

const FileViewer = ({ file, className = '' }: FileViewerProps) => {
  const [fileLoaded, setFileLoaded] = useState(file.googleDriveFileUrl ? true : false);
  const documentType = getDocumentType(file.name);
  const fileUrl = useMemo(() => getFileEmbedUrl(file.rawName), [file.rawName]);
  const previewUrl = useMemo(() => getFilePreviewUrl(file.rawName), [file.rawName]);

  useEffect(() => {
    if (documentType === FileCategory.DATA) {
      setFileLoaded(true);
    }
  }, [documentType, file.googleDriveFileUrl]);

  const handleFileLoaded = useCallback(() => {
    setFileLoaded(true);
  }, []);

  const content = getContent({
    fileUrl,
    previewUrl,
    fileName: file.name,
    documentType,
    onFileLoaded: handleFileLoaded,
  });

  if (file.googleDriveFileUrl && !file.isFileSupported) {
    return (
      <div className="filePreviewContainer tw-flex tw-items-center tw-justify-center tw-h-[calc(100%-52px)] tw-bg-[#525659]">
        <div className="tw-flex tw-flex-col tw-items-center tw-text-white tw-text-center tw-gap-[20px]">
          {/* Message Section */}
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-[5px]">
            <div className=" tw-flex tw-items-center tw-font-[420] tw-gap-[10px] tw-leading-[24px]">
              <Icon className="tw-text-[24px]" icon={ICONS.info} iconType={ICON_TYPES.outlined} />
              <span className="tw-text-[16px] tw-font-brandonTextRegular ">
                Couldn’t preview this file.
              </span>
            </div>
            <span className="tw-text-[12px] tw-leading-[18px] tw-font-[390]">
              This file is not supported by WebApp. Try opening on Google Drive instead.
            </span>
          </div>

          {/* Button Section */}
          <AnchorLink
            className={cn(
              'tw-h-[33px] tw-rounded-lg  tw-flex tw-items-center tw-justify-center',
              'tw-bg-white tw-w-[178px] tw-text-cfo-black tw-text-[14px] lw-leading-[21px]',
              'tw-py-2 tw-px-4 tw-gap-[5px]',
              'tw-no-underline hover:tw-no-underline'
            )}
            href={file.googleDriveFileUrl}
            target="_blank"
          >
            <Icon
              icon={ICONS.gdrive({
                className: 'tw-text-cfo-black',
                height: '16',
                width: '16',
              })}
            />
            Open on Google Drive
          </AnchorLink>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div
        className={cn(
          'filePreviewContainer',
          'tw-relative tw-overflow-auto tw-scroll-styles',
          'tw-h-[calc(100%-52px)]',
          'tw-flex tw-items-center tw-justify-center',
          documentType === FileCategory.DATA && 'tw-bg-cfo-grey-100',
          documentType === FileCategory.IMAGE ? 'tw-overflow-none' : 'tw-overflow-auto',
          className
        )}
      >
        {!fileLoaded && (
          <SectionLoader
            loaderType={LOADER_TYPE.screen}
            spinnerClasses={clsx('tw-translate-x-[-5%] tw-translate-y-[-5%]')}
          />
        )}
        <div className={clsx('tw-p-0')}>
          <div id="documentImage" className="">
            {content}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default FileViewer;
